import { useMemo } from 'react';

import type { GetLeadDetailsQuery } from '@graphql/__generated__/graphql';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  userCanManageLead,
  userCanViewLeadDetails,
  userHasClaimedLeadAssignment,
} from '@modules/_shared/utils/listingPermissions';

type Lead = NonNullable<GetLeadDetailsQuery['leads_by_pk']>;

type UseLeadPermissionsParams = {
  lead: Lead | null | undefined;
};

export type LeadPermissions = {
  canManageLead: boolean;
  canManageProfileLead: boolean;
  canView: boolean;
  isAdmin: boolean;
  restrictedAgentView: boolean;
  currentUserId: string | null;
};

export const useLeadPermissions = ({
  lead,
}: UseLeadPermissionsParams): LeadPermissions => {
  const { me } = useAppData();

  const canView = useMemo(() => {
    if (!lead) {
      return false;
    }

    return userCanViewLeadDetails(me, lead);
  }, [lead, me]);

  const isAdmin = me?.is_admin ?? false;

  // Agent has pending acceptance (not claimed yet)
  // Note: We don't check refused_at here because agents who refuse a lead
  // lose access to it (filtered out by userCanViewLeadDetails), so they won't
  // reach this point in the code
  const hasPendingAcceptance = (lead?.lead_agents ?? []).some(
    agent => agent.user_id === me?.id && agent.claimed_at == null,
  );

  // Restricted view for non-admin agents with pending acceptance
  const restrictedAgentView = !isAdmin && hasPendingAcceptance;

  const currentUserId = me?.id ?? null;
  const canManageProfileLead =
    lead?.origin_enquiry_id != null &&
    userHasClaimedLeadAssignment(me, lead.lead_agents ?? [], {
      excludePassed: true,
    });
  const canManageLead = userCanManageLead(me, lead);

  return {
    canManageLead,
    canManageProfileLead,
    canView,
    isAdmin,
    restrictedAgentView,
    currentUserId,
  };
};
